// ----- Imports ----- //
import { getViewId } from '@guardian/ophan-tracker-js';
import * as z from 'zod/mini';
import { testIsActive } from 'helpers/abTests/abtest';
import { get as getCookie } from 'helpers/storage/cookie';
import { getSession, setSession } from 'helpers/storage/storage';
import { getAllQueryParams, getAllQueryParamsWithExclusions, getQueryParameter, } from 'helpers/urls/url';
import { deserialiseJsonObject } from 'helpers/utilities/utilities';
// ----- Setup ----- //
const ACQUISITIONS_PARAM = 'acquisitionData';
const ACQUISITIONS_STORAGE_KEY = 'acquisitionData';
export const referrerAcquisitionDataSchema = z.object({
    campaignCode: z.optional(z.string()),
    referrerPageviewId: z.optional(z.string()),
    referrerUrl: z.optional(z.string()),
    componentId: z.optional(z.string()),
    componentType: z.optional(z.string()),
    source: z.optional(z.string()),
    abTests: z.optional(z.array(z.object({ name: z.string(), variant: z.string() }))),
    queryParameters: z.optional(z.array(z.object({ name: z.string(), value: z.string() }))),
    labels: z.optional(z.array(z.string())),
    isRemote: z.optional(z.boolean()),
});
// ----- Functions ----- //
// Stores the acquisition data in sessionStorage.
function storeReferrerAcquisitionDataInSessionStorage(referrerAcquisitionData) {
    try {
        setSession(ACQUISITIONS_STORAGE_KEY, referrerAcquisitionData);
        return true;
    }
    catch (err) {
        return false;
    }
}
const toAcquisitionQueryParameters = (parameters) => {
    return parameters.map(([name, value]) => ({ name, value }));
};
const participationsToAcquisitionABTest = (participations) => {
    const activeTests = Object.entries(participations).filter(testIsActive);
    return activeTests.map(([name, variant]) => ({
        name,
        variant,
    }));
};
// Builds the acquisition object from data and other sources.
function buildReferrerAcquisitionData(acquisitionData) {
    // This was how referrer pageview id used to be passed.
    const referrerPageviewId = acquisitionData.referrerPageviewId ??
        getQueryParameter('REFPVID');
    const campaignCode = getQueryParameter('INTCMP') || getQueryParameter('CMP') || undefined;
    const parameterExclusions = [
        'REFPVID',
        'INTCMP',
        'acquisitionData',
        'contributionValue',
        'contribType',
        'currency',
    ];
    const queryParameters = acquisitionData.queryParameters ??
        toAcquisitionQueryParameters(getAllQueryParamsWithExclusions(parameterExclusions));
    const source = campaignCode && /^PPC_/i.test(campaignCode)
        ? 'PPC'
        : acquisitionData.source;
    return {
        referrerPageviewId,
        campaignCode,
        referrerUrl: acquisitionData.referrerUrl,
        componentId: acquisitionData.componentId,
        componentType: acquisitionData.componentType,
        source,
        abTests: (acquisitionData.abTest
            ? [acquisitionData.abTest]
            : acquisitionData.abTests),
        queryParameters: queryParameters.length > 0 ? queryParameters : [],
        labels: acquisitionData.labels,
        isRemote: acquisitionData.isRemote,
    };
}
const getOphanIds = () => ({
    pageviewId: getViewId(),
    browserId: getCookie('bwid'),
});
function getSupportAbTests(participations) {
    return participationsToAcquisitionABTest(participations);
}
const getAbTests = (referrerAcquisitionData, participations) => {
    const alltests = [
        ...participationsToAcquisitionABTest(participations),
        ...(referrerAcquisitionData.abTests ?? []),
    ];
    return alltests.reduce((acc, abTest) => acc.find((test) => test.name === abTest.name)
        ? acc
        : acc.concat([abTest]), []);
};
function derivePaymentApiAcquisitionData(referrerAcquisitionData, nativeAbParticipations, postalCode, countryId) {
    const ophanIds = getOphanIds();
    const abTests = getAbTests(referrerAcquisitionData, nativeAbParticipations);
    const campaignCodes = referrerAcquisitionData.campaignCode
        ? [referrerAcquisitionData.campaignCode]
        : [];
    return {
        platform: 'SUPPORT',
        browserId: ophanIds.browserId,
        pageviewId: ophanIds.pageviewId,
        referrerPageviewId: referrerAcquisitionData.referrerPageviewId,
        referrerUrl: referrerAcquisitionData.referrerUrl,
        componentId: referrerAcquisitionData.componentId,
        campaignCodes,
        componentType: referrerAcquisitionData.componentType,
        source: referrerAcquisitionData.source,
        abTests,
        gaId: getCookie('_ga'),
        queryParameters: referrerAcquisitionData.queryParameters,
        labels: referrerAcquisitionData.labels,
        postalCode: postalCode,
        countryId,
    };
}
// Reads the acquisition data from sessionStorage.
function getReferrerAcquisitionDataFromSessionStorage() {
    return getSession(ACQUISITIONS_STORAGE_KEY, referrerAcquisitionDataSchema);
}
function getAcquisitionDataFromUtmParams() {
    // Same order of fields as https://reader-revenue-lynx.s3.eu-west-1.amazonaws.com/v3.html
    const utmCampaign = getQueryParameter('utm_campaign');
    const utmContent = getQueryParameter('utm_content');
    const utmTerm = getQueryParameter('utm_term');
    const utmSource = getQueryParameter('utm_source');
    const utmMedium = getQueryParameter('utm_medium');
    // All must be present in the URL for them to be accepted
    if (utmCampaign !== '' &&
        utmContent !== '' &&
        utmTerm !== '' &&
        utmSource !== '' &&
        utmMedium !== '') {
        return {
            campaignCode: utmCampaign,
            abTest: {
                name: utmContent,
                variant: utmTerm,
            },
            source: utmSource,
            componentType: utmMedium,
            queryParameters: toAcquisitionQueryParameters(getAllQueryParams()),
        };
    }
    return;
}
// Reads the acquisition data from the &acquistionData param containing a serialised JSON string.
function getAcquisitionDataFromAcquisitionDataParam() {
    if (getQueryParameter(ACQUISITIONS_PARAM)) {
        return deserialiseJsonObject(getQueryParameter(ACQUISITIONS_PARAM));
    }
    return null;
}
// Generates appropriate acquisition data from the various, known, PPC params
function getAcquisitionDataFromPPCParams() {
    if (getQueryParameter('gclid')) {
        return {
            source: 'PPC',
            campaignCode: 'Google_Adwords',
            queryParameters: toAcquisitionQueryParameters(getAllQueryParams()),
        };
    }
    return null;
}
// Returns the acquisition metadata, either from query param or sessionStorage.
// Also stores in sessionStorage if not present or new from param.
function getReferrerAcquisitionData() {
    // Read acquisitonData from the various query params, or from sessionStorage, in the following precedence
    const candidateAcquisitionData = getAcquisitionDataFromAcquisitionDataParam() ??
        getAcquisitionDataFromUtmParams() ??
        getAcquisitionDataFromPPCParams() ??
        getReferrerAcquisitionDataFromSessionStorage();
    const referrerAcquisitionData = buildReferrerAcquisitionData(candidateAcquisitionData ?? {});
    storeReferrerAcquisitionDataInSessionStorage(referrerAcquisitionData);
    return referrerAcquisitionData;
}
// ----- Exports ----- //
export { getReferrerAcquisitionData, getOphanIds, participationsToAcquisitionABTest, derivePaymentApiAcquisitionData, getSupportAbTests, };
